import { useState, FC } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Modal, Typography, Icon } from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { useAppThunkDispatch } from 'state/store';
import { selectNumberOfSelectedTasks } from 'state/ProjectTasksList/export/exportSlice';
import {
  bulkDeleteTasks,
  selectBulkDeleteLoading,
} from 'state/ProjectTasksList/bulkActions/bulkDelete/bulkDeleteSlice';
import { setShouldRefreshTasks } from 'state/ProjectTasksList/actions/actionsSlice';
import { resetSelectedTasks } from 'state/ProjectTasksList/export/exportSlice';

interface BulkDeleteProps {
  disabled?: boolean;
}

const BulkDelete: FC<BulkDeleteProps> = ({ disabled }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const numberOfSelectedTasks = useSelector(selectNumberOfSelectedTasks);
  const dispatch = useAppThunkDispatch();
  const bulkDeleteLoading = useSelector(selectBulkDeleteLoading);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleConfirmDelete = async () => {
    if (!projectId) return;

    await dispatch(
      bulkDeleteTasks({
        projectId,
        config: {
          headers: {
            'notification-type': 'in_app',
          },
        },
      })
    );
    dispatch(setShouldRefreshTasks(true));
    dispatch(resetSelectedTasks());
    setIsOpen(false);
  };

  return (
    <>
      <Button
        variant='tertiary'
        className='group px-2 py-1 text-neutral-white hover:text-neutral-white hover:bg-primary-dark hover:border-primary-dark active:text-neutral-white active:bg-primary-dark active:border-primary-dark'
        onClick={() => setIsOpen(true)}
        data-cy='bulk-delete__action-button'
        disabled={disabled}
      >
        <Icon
          name='trash'
          className='text-xl mr-1 text-error-light group-hover:text-neutral-white'
        />
        {intl.get('DELETE')}
      </Button>

      {isOpen && (
        <Modal
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          title={intl.get('TASKS.BULK_DELETE_MODAL.TITLE', {
            num: numberOfSelectedTasks,
          })}
          aria-label='bulk-delete-modal'
          titleIcon={{ name: 'trash', className: 'text-error-dark' }}
          size='small'
          actionButtons={[
            {
              children: intl.get('DELETE'),
              variant: 'primary',
              color: 'danger',
              onClick: handleConfirmDelete,
              loading: bulkDeleteLoading,
              'data-cy': 'bulk-delete__confirm-button',
            },
            {
              children: intl.get('CANCEL'),
              variant: 'tertiary',
              onClick: () => setIsOpen(false),
              'data-cy': 'bulk-delete__cancel-button',
            },
          ]}
        >
          <Typography variant='body'>
            {intl.get('TASKS.BULK_DELETE_MODAL.BODY')}
          </Typography>
        </Modal>
      )}
    </>
  );
};

export default BulkDelete;
